import React, { useState } from 'react';
import {
  Twitter,
  Instagram,
  Book,
  ShoppingBag,
  Heart,
  Globe,
  Youtube,
  Music,
  Link as LinkIcon
} from 'lucide-react';

export type SocialPlatform = 
  | 'twitter'
  | 'instagram'
  | 'goodreads'
  | 'amazon_author'
  | 'patreon'
  | 'website'
  | 'youtube'
  | 'tiktok'
  | 'linktree';

export type SocialLinks = Partial<Record<SocialPlatform, string>>;

export const PLATFORM_CONFIG = {
  twitter: {
    icon: Twitter,
    label: 'Twitter',
    color: 'text-[#1DA1F2] hover:text-[#0C85D0]'
  },
  instagram: {
    icon: Instagram,
    label: 'Instagram',
    color: 'text-[#E4405F] hover:text-[#D32D4A]'
  },
  goodreads: {
    icon: Book,
    label: 'Goodreads',
    color: 'text-[#553B08] hover:text-[#372607]'
  },
  amazon_author: {
    icon: ShoppingBag,
    label: 'Amazon Author Central',
    color: 'text-[#FF9900] hover:text-[#E68A00]'
  },
  patreon: {
    icon: Heart,
    label: 'Patreon',
    color: 'text-[#FF424D] hover:text-[#E63E47]'
  },
  website: {
    icon: Globe,
    label: 'Website',
    color: 'text-accent1 hover:text-accent2'
  },
  youtube: {
    icon: Youtube,
    label: 'YouTube',
    color: 'text-[#FF0000] hover:text-[#CC0000]'
  },
  tiktok: {
    icon: Music,
    label: 'TikTok',
    color: 'text-[#000000] hover:text-[#333333]'
  },
  linktree: {
    icon: LinkIcon,
    label: 'Linktree',
    color: 'text-[#43E660] hover:text-[#39C251]'
  }
} as const;

export const CORE_PLATFORMS: SocialPlatform[] = [
  'twitter',
  'instagram',
  'goodreads',
  'amazon_author',
  'patreon'
];

export const EXTRA_PLATFORMS: SocialPlatform[] = [
  'youtube',
  'tiktok',
  'linktree'
];

type SocialLinksProps = {
  links: SocialLinks;
  showAll?: boolean;
};

export default function SocialLinks({ links, showAll = false }: SocialLinksProps) {
  const [showExtra, setShowExtra] = useState(false);
  
  const visiblePlatforms = showAll || showExtra 
    ? [...CORE_PLATFORMS, ...EXTRA_PLATFORMS]
    : CORE_PLATFORMS;

  const hasExtraLinks = EXTRA_PLATFORMS.some(platform => links[platform]);
  
  return (
    <div className="flex flex-wrap items-center gap-3">
      {visiblePlatforms.map(platform => {
        const link = links[platform];
        if (!link) return null;

        const config = PLATFORM_CONFIG[platform];
        const Icon = config.icon;

        return (
          <a
            key={platform}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${config.color}`}
            title={`Visit ${config.label}`}
          >
            <Icon className="w-5 h-5" />
          </a>
        );
      })}

      {!showAll && hasExtraLinks && !showExtra && (
        <button
          onClick={() => setShowExtra(true)}
          className="p-2 text-accent1 hover:text-accent2 hover:bg-gray-100 rounded-full transition-colors"
          title="Show more links"
        >
          <LinkIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}